/*
 *	Copyright (c) 2024, Signaloid.
 *
 *	Permission is hereby granted, free of charge, to any person obtaining a copy
 *	of this software and associated documentation files (the "Software"), to deal
 *	in the Software without restriction, including without limitation the rights
 *	to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 *	copies of the Software, and to permit persons to whom the Software is
 *	furnished to do so, subject to the following conditions:
 *
 *	The above copyright notice and this permission notice shall be included in all
 *	copies or substantial portions of the Software.
 *
 *	THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 *	IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 *	FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 *	AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 *	LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 *	OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 *	SOFTWARE.
 */

import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'
import * as signaloidClient from '@signaloid/cloud-compute-engine-client'
import { Auth } from 'aws-amplify'
import { useSnackbarStore } from '@/stores/snackbar.store'

const routes: RouteRecordRaw[] = [
	{
		path: '/',
		name: 'HomePage',
		component: () => import('@/views/HomeView.vue'),
		meta: {
			requiresAuth: true
		}
	},
	{
		path: '/login',
		name: 'Login',
		component: () => import('@/views/LoginView.vue'),
		meta: {
			requiresAuth: false
		}
	},
	{
		path: '/visualize/:boxPlotId/:targetPointCloudName',
		name: 'Visualize',
		component: () => import('@/views/Visualize.vue'),
		meta: {
			requiresAuth: true
		}
	},
	{
		path: '/:catchAll(.*)',
		redirect: '/'
	}
]

const router = createRouter({
	history: createWebHistory(),
	routes
})

router.beforeEach(async (to, from, next) => {
	console.log(import.meta.env.VITE_APP_SIGNALOID_API)
	signaloidClient.configureDeploymentEnvironment(import.meta.env.VITE_APP_SIGNALOID_API)

	if (to.matched.some((record) => record.meta.requiresAuth)) {
		try {
			const user = await signaloidClient.currentAuthenticatedUser()
			if (user) {
				await signaloidClient.refreshAuthentication()
				const token = await getAuthToken()
				const payload = parseJwt(token)
				console.log(payload['cognito:groups'].includes('CustomerGroupB'))
				if (!payload['cognito:groups'].includes('CustomerGroupB')) {
					localStorage.clear()
					next({ name: 'Login' })
					return
				}
				console.log('Payload is: ', payload)
				console.log('User is: ', user)
				next()
			} else {
				localStorage.clear()
				next({ name: 'Login' })
			}
		} catch (error) {
			localStorage.clear()
			next({ name: 'Login' })
		}
	} else {
		console.log('No auth required')
		next()
	}
})

async function getAuthToken() {
	const currentSession = await Auth.currentSession() // Make sure you have Auth imported
	return currentSession.getIdToken().getJwtToken()
}

function parseJwt(token: string) {
	const base64Url = token.split('.')[1]
	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
	const jsonPayload = decodeURIComponent(
		window
			.atob(base64)
			.split('')
			.map(function (c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
			})
			.join('')
	)

	return JSON.parse(jsonPayload)
}

export default router
